<template>
  <div class="ops-op" data-test="ops-operatory">
    <!-- The Operatory is a full-screen view (no app shell, no page header), so it draws its own top bar: the title, the pinned room (a tap
         reopens the room picker), the clock and — in the `picker` slot, filled by the page — the view picker. -->
    <header class="ops-op__top" data-test="ops-op-top">
      <h2 class="ops-op__title" data-test="ops-op-title">Practice Ops</h2>
      <button
        v-if="room"
        type="button"
        class="ops-op__chip"
        :aria-label="`Pinned to ${room.label}. Change room`"
        title="Change room"
        data-test="ops-op-room-chip"
        @click="choosing = true"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 17v5" /><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" /></svg>
        <span class="ops-op__chipname" data-test="ops-op-room-name">{{ room.label }}</span>
      </button>
      <span class="ops-op__clock" data-test="ops-op-clock">{{ clock }}<span class="ops-op__meridiem">{{ meridiem }}</span></span>
      <div class="ops-op__picker" data-test="ops-op-picker"><slot name="picker"></slot></div>
    </header>

    <div v-if="skeleton" class="ops-op__skel" aria-hidden="true"><span></span><span></span></div>

    <div v-else-if="!board.rooms.length" class="ops-op__none" data-test="ops-op-no-rooms">No rooms are set up yet.</div>

    <!-- This screen has no room yet (or the saved one is no longer a room): pick it once; the device remembers. -->
    <section v-else-if="!room || choosing" class="ops-op__pick" data-test="ops-op-pick">
      <h3 class="ops-op__pick-title">Which room is this screen in?</h3>
      <p class="ops-op__pick-text">Pick it once. This device remembers it.</p>
      <div class="ops-op__picks">
        <button
          v-for="r in board.rooms"
          :key="r.opNum"
          type="button"
          class="ops-op__pickbtn"
          :class="{ 'ops-op__pickbtn--on': room && room.opNum === r.opNum }"
          :aria-pressed="room && room.opNum === r.opNum ? 'true' : null"
          :data-test="`ops-op-pick-${r.opNum}`"
          @click="pick(r.opNum)"
        >{{ r.label }}</button>
      </div>
      <button v-if="room" type="button" class="ops-op__pickkeep" data-test="ops-op-pick-keep" @click="choosing = false">Keep {{ room.label }}</button>
    </section>

    <template v-else>
      <div class="ops-op__stage" :class="stageClass" data-test="ops-op-stage">
        <!-- The pinned room: the drop target. One patient per room: a second card dropped on it is refused by the page. -->
        <OpsColumn
          variant="room"
          :col-id="room.opNum"
          :cards="room.cards"
          :hover="hoverCol === String(room.opNum)"
          :busy="roomBusy"
          :hover-text="`Drop to seat in ${room.label}`"
          :move="handleMove"
          @add="onAdd"
          @drag-start="onDragStart"
          @drag-end="onDragEnd"
        >
          <template #card="{ card }">
            <div class="ops-op__occ">
              <OpsPatientCard v-bind="cardProps(card, 'lg')" v-on="cardListeners" />
              <div class="ops-op__facts" data-test="ops-op-facts">{{ factsFor(card) }}</div>
            </div>
          </template>

          <template #empty>
            <div class="ops-op__open" data-test="ops-op-open">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#9ca4a2" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 4h3a2 2 0 0 1 2 2v14" /><path d="M2 20h3" /><path d="M13 20h9" /><path d="M10 12v.01" /><path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z" /></svg>
              <div class="ops-op__open-title">Room open</div>
              <div class="ops-op__open-sub" data-test="ops-op-open-sub">{{ openText }}</div>
              <div class="ops-op__open-hint">Drag the next patient here from the queue below.</div>
              <button type="button" class="ops-op__btn ops-op__btn--primary ops-op__seat" :disabled="!next" data-test="ops-op-seat-next" @click="seatNextNow">{{ seatLabel }}</button>
            </div>
          </template>
        </OpsColumn>

        <div class="ops-op__actions">
          <button type="button" class="ops-op__btn ops-op__btn--secondary" data-test="ops-op-send" @click="onSend">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
            Send alert
          </button>
          <button type="button" class="ops-op__btn ops-op__btn--primary" :disabled="!seated" data-test="ops-op-checkout" @click="onCheckout">Check out</button>
        </div>
      </div>

      <section class="ops-op__queue" aria-label="Up next" data-test="ops-op-queue">
        <div class="ops-op__queuehead">
          <span class="ops-op__queuetitle">Up next · Check-in</span>
          <span class="ops-op__queuesub">{{ queueSub }}</span>
          <span class="ops-op__queuewarn">{{ queueWarn }}</span>
        </div>
        <OpsColumn
          variant="strip"
          col-id="checkin"
          :cards="board.checkin"
          :hover="hoverCol === 'checkin'"
          empty-text="No one waiting"
          hover-text="Drop to move back"
          :move="handleMove"
          @add="onAdd"
          @drag-start="onDragStart"
          @drag-end="onDragEnd"
        >
          <template #card="{ card }"><OpsPatientCard v-bind="cardProps(card, 'touch')" v-on="cardListeners" /></template>
        </OpsColumn>
      </section>

      <section class="ops-op__others" aria-label="Other rooms" data-test="ops-op-others">
        <div class="ops-op__otherstitle">Other rooms</div>
        <div class="ops-op__othersgrid">
          <button
            v-for="o in others"
            :key="o.opNum"
            type="button"
            class="ops-op__other"
            :data-test="`ops-op-other-${o.opNum}`"
            @click="pick(o.opNum)"
          >
            <span class="ops-op__otheredge" :style="{ background: o.stripe }" aria-hidden="true"></span>
            <span class="ops-op__othername">{{ o.label }}</span>
            <span class="ops-op__otherrow">
              <span class="ops-op__otherwho" :class="{ 'ops-op__otherwho--open': !o.who }">{{ o.who || 'Open' }}</span>
              <span class="ops-op__othertime">{{ o.time }}</span>
            </span>
          </button>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
/**
 * The Practice Ops Operatory view (plan "Design review" → Tablet / Operatory): an iPad pinned to ONE room.
 *
 *   • FULL SCREEN. `?view=operatory` hides the app shell (Layout.vue / App.vue, wallShell.js), so the view draws its OWN top bar — the
 *     design's "Practice Ops", the pinned-room chip, the clock — and ends it with the view picker, handed in by the page through the
 *     `picker` slot (Board / Phone navigate back to the shell).
 *   • THE ROOM. Chosen from localStorage `teamio_practice_ops_op` (viewPref.js readOp / writeOp). Unset, or no longer a room ⇒ a
 *     picker; a tap on the top bar's room chip reopens that picker, and so does nothing else but a tap on "Other rooms" (which switches
 *     the room directly). Every change is persisted. This view owns that preference — nothing else reads it.
 *   • OCCUPIED: the room's card at `lg` plus the facts line "Dr. Kapoor · seated 9:23 · booked for 9:15". EMPTY: "Room open" and
 *     "Last patient left N min ago" (from the room's lastLeftMs, which the board reads off the Check-out card's lastRoom) or "No one
 *     seated yet today", the drop zone, and "Seat next: <Last>, <First>" — the Check-in patient who has waited longest (moves.js
 *     seatNext; NO Ready gate) — or a disabled "No one waiting".
 *   • Big Send alert / Check out buttons for the seated patient, the "Up next · Check-in" queue at `touch` size (drag a card into the
 *     room), and an "Other rooms" strip (a tap switches the pinned room).
 *
 * It decides and writes NOTHING. Every move — a drop, the card's ⋯, Check out, Seat next — is one event, `request-move({ card, to })`
 * (`to` is 'checkin' | 'checkout' | an OperatoryNum); the PAGE runs it through moves.js planMove (the occupied-room refusal and its
 * toast) and the classic moveCard, so a Seat next and a Board drop are the same code.
 *
 *   request-move · ping-card(card) · ping-room(opNum) · alert-click({ card, key, el }) · dismiss-card(card) · drag-start · drag-end
 *
 * ⛔ No prop is named `patNum`, `siteID`, `loading` or `allSettings` (mixin.js computeds shadow them).
 */
import OpsColumn from './OpsColumn.vue';
import OpsPatientCard from './OpsPatientCard.vue';
import dropTracking from './dropTracking';
import { seatNext, seatNextLabel, cardLastFirst } from '@/utils/practiceOps/moves';
import {
  clockText, meridiemText, operatoryFacts, lastLeftText, arrivedReadyLine, waitingLine,
} from '@/utils/practiceOps/boardText';
import { readOp, writeOp } from '@/utils/practiceOps/viewPref';
import { browserStorage } from '@/utils/followups/uiVersion';

export default {
  name: 'OpsOperatory',

  components: { OpsColumn, OpsPatientCard },

  mixins: [dropTracking],

  props: {
    /** board.js buildBoard() */
    board: { type: Object, required: true },
    zone: { type: String, default: '' },
    nowMs: { type: Number, default: 0 },
    alertPayload: { type: Object, default: null },
    viewSettings: { type: Object, default: null },
    showAlertIcons: { type: Boolean, default: true },
    skeleton: { type: Boolean, default: false },
    /** card → '#rrggbb' (the provider's colour), card → { proc, note }, card → boolean, card → the provider's name for the facts line */
    stripeFor: { type: Function, default: () => '' },
    textFor: { type: Function, default: card => ({ proc: card.procDescript || '', note: card.note || '' }) },
    birthdayFor: { type: Function, default: () => false },
    providerFor: { type: Function, default: () => '' },
  },

  data() {
    return {
      /** The pinned room (an OperatoryNum) or null — per device (localStorage). */
      pinned: readOp(browserStorage()),
      /** The room picker is open although a room is pinned (a tap on the top bar's room chip). */
      choosing: false,
    };
  },

  computed: {
    room() {
      return this.board.rooms.find(r => r.opNum === this.pinned) || null;
    },

    /** The patient the buttons act on: the first card in the room (a room holding 2+ is old data; each card still has its own ⋯). */
    seated() {
      return this.room && this.room.cards.length ? this.room.cards[0] : null;
    },

    /** A card from elsewhere is over an OCCUPIED room: the drop will be refused (amber). */
    roomBusy() {
      return !!this.room && this.room.occupied
        && this.hoverCol === String(this.room.opNum) && this.dragFrom !== String(this.room.opNum);
    },

    stageClass() {
      if (this.roomBusy) return 'ops-op__stage--busy';
      if (this.room && this.hoverCol === String(this.room.opNum)) return 'ops-op__stage--hover';
      return this.room && this.room.occupied ? 'ops-op__stage--full' : 'ops-op__stage--open';
    },

    clock() {
      return clockText(this.nowMs, this.zone);
    },

    meridiem() {
      return meridiemText(this.nowMs, this.zone);
    },

    /** Who Seat next would seat: the Check-in patient with the longest live wait (no Ready gate), or null. */
    next() {
      return seatNext(this.board.checkin, this.nowMs);
    },

    seatLabel() {
      return seatNextLabel(this.next);
    },

    openText() {
      return lastLeftText(this.room, this.nowMs);
    },

    queueSub() {
      return arrivedReadyLine(this.board.counts);
    },

    queueWarn() {
      const w = waitingLine(this.board.counts, this.board.thresholdMin);
      return w.a ? `${w.a}${w.b}` : '';
    },

    /** Where "Move to…" can send a card: Check-in and every free room (the card drops its own place). */
    moveTargets() {
      const rooms = this.board.rooms.filter(r => !r.occupied).map(r => ({ id: r.opNum, label: r.label }));
      return [{ id: 'checkin', label: 'Check-in' }].concat(rooms);
    },

    /** The "Other rooms" strip: every room but the pinned one — who is in it and for how long. */
    others() {
      return this.board.rooms.filter(r => r.opNum !== this.pinned).map((r) => {
        const first = r.cards[0] || null;
        return {
          opNum: r.opNum,
          label: r.label,
          who: first ? this.shortWho(first) : '',
          time: first && typeof first.inChairMin === 'number' ? `${first.inChairMin} min` : '',
          stripe: (first && this.stripeFor(first)) || '#dfe4e3',
        };
      });
    },

    cardListeners() {
      return {
        ping: card => this.$emit('ping-card', card),
        'alert-click': payload => this.$emit('alert-click', payload),
        move: ({ card, to }) => this.$emit('request-move', { card, to }),
        checkout: card => this.$emit('request-move', { card, to: 'checkout' }),
        dismiss: card => this.$emit('dismiss-card', card),
      };
    },
  },

  methods: {
    /** Pin a room (from the picker, the select or the Other rooms strip) and remember it on this device. */
    pick(value) {
      const n = Number(value);
      if (!Number.isInteger(n) || n <= 0) return;
      this.pinned = n;
      this.choosing = false;
      writeOp(browserStorage(), n);
    },

    /** "Okafor, D." — a compact name for the Other rooms strip. */
    shortWho(card) {
      const full = cardLastFirst(card);
      const comma = full.indexOf(',');
      if (comma === -1) return full;
      const initial = full.slice(comma + 1).trim().charAt(0);
      return initial ? `${full.slice(0, comma)}, ${initial}.` : full.slice(0, comma);
    },

    factsFor(card) {
      return operatoryFacts(card, this.providerFor(card), this.zone);
    },

    /** Everything one OpsPatientCard is given (a v-bind object). */
    cardProps(card, size) {
      const text = this.textFor(card) || {};
      return {
        card,
        size,
        stripe: this.stripeFor(card) || '',
        proc: text.proc || '',
        note: text.note || '',
        zone: this.zone,
        alertPayload: this.alertPayload,
        viewSettings: this.viewSettings,
        showAlertIcons: this.showAlertIcons,
        birthday: !!this.birthdayFor(card),
        moveTargets: this.moveTargets,
        canDismiss: card.column === 'checkout' && !card.dismissed,
      };
    },

    /** A card from another list landed on `to` (this room, or the queue). */
    onAdd({ to, card }) {
      this.$emit('request-move', { card, to });
    },

    /** Seat next: the SAME request a drop into this room makes — the page applies the one-patient-per-room rule and the toast. */
    seatNextNow() {
      if (this.next && this.room) this.$emit('request-move', { card: this.next, to: this.room.opNum });
    },

    onCheckout() {
      if (this.seated) this.$emit('request-move', { card: this.seated, to: 'checkout' });
    },

    /** Send alert: for the seated patient (their room), else for THIS room. */
    onSend() {
      if (this.seated) this.$emit('ping-card', this.seated);
      else if (this.room) this.$emit('ping-room', this.room.opNum);
    },
  },
};
</script>

<style lang="scss" scoped>
.ops-op {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.ops-op__none {
  padding: 48px 16px;
  text-align: center;
  border: 1.5px dashed #d5dcda;
  border-radius: 14px;
  color: #707876;
  font-size: 14px;
}

.ops-op__skel {
  display: flex;
  flex-direction: column;
  gap: 12px;

  span {
    display: block;
    height: 180px;
    border-radius: 18px;
    background: #e3e8e7;
  }
}

// ── the room picker ──
.ops-op__pick {
  max-width: 560px;
  margin: 24px auto;
  padding: 24px;
  text-align: center;
  border: 1px solid #dfe4e3;
  border-radius: 16px;
  background: #ffffff;
}

.ops-op__pick-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 800;
}

.ops-op__pick-text {
  margin: 0 0 16px;
  color: #707876;
}

.ops-op__picks {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.ops-op__pickbtn {
  min-height: 56px;
  padding: 0 12px;
  border: 1.5px solid #dfe4e3;
  border-radius: 12px;
  background: #f7faf9;
  color: #0c0f0e;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  &:hover { border-color: #0f7259; background: #e9f6f1; }
}

.ops-op__pickbtn--on { border-color: #0f7259; background: #e9f6f1; color: #0b5c49; }

.ops-op__pickkeep {
  margin-top: 14px;
  padding: 8px 14px;
  border: 0;
  background: transparent;
  color: #0f7259;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

// ── the top bar: title, pinned room, clock … the view picker ──
.ops-op__top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.ops-op__title {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.ops-op__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 260px;
  height: 34px;
  padding: 0 14px 0 11px;
  border: 0;
  border-radius: 999px;
  background: #e9f6f1;
  color: #0b5c49;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;

  svg { display: block; flex: none; }
  &:hover { background: #dcf0e8; }
  &:focus-visible { outline: 2px solid #0f7259; outline-offset: 2px; }
}

.ops-op__chipname {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.ops-op__picker { flex: none; }

.ops-op__clock {
  margin-left: auto;
  font-size: 22px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.ops-op__meridiem {
  margin-left: 4px;
  font-size: 13px;
  color: #707876;
  font-weight: 700;
}

// ── the room panel ──
.ops-op__stage {
  display: flex;
  gap: 16px;
  min-height: 260px;
  padding: 16px;
  box-sizing: border-box;
  border: 2px solid #dfe4e3;
  border-radius: 18px;
  background: #ffffff;
}

.ops-op__stage--open { border-style: dashed; border-color: #c8cfcd; background: #f7faf9; }
.ops-op__stage--hover { border-style: dashed; border-color: #0f7259; background: #e9f6f1; }
.ops-op__stage--busy { border-color: #f59e0b; background: #fef6e7; }

.ops-op__occ {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ops-op__facts {
  padding-left: 4px;
  font-size: 14px;
  color: #3a4240;
}

.ops-op__open {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 0;
  text-align: center;

  svg { display: block; }
}

.ops-op__open-title {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.ops-op__open-sub {
  font-size: 16px;
  color: #707876;
}

.ops-op__open-hint {
  margin-top: 4px;
  font-size: 14px;
  color: #3a4240;
}

.ops-op__seat { margin-top: 8px; min-width: 220px; }

.ops-op__actions {
  flex: none;
  width: 230px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
}

.ops-op__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 52px;
  padding: 0 18px;
  border: 1.5px solid transparent;
  border-radius: 12px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;

  svg { display: block; flex: none; }
  &:disabled { opacity: 0.45; cursor: default; }
}

.ops-op__btn--primary {
  background: #0f7259;
  border-color: #0f7259;
  color: #ffffff;

  &:hover:not(:disabled) { background: #0b5c49; border-color: #0b5c49; }
}

.ops-op__btn--secondary {
  background: #ffffff;
  border-color: #b9dccd;
  color: #0b5c49;

  &:hover:not(:disabled) { background: #e9f6f1; }
}

// ── the queue ──
.ops-op__queue {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.ops-op__queuehead {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
}

.ops-op__queuetitle {
  font-size: 16px;
  font-weight: 800;
}

.ops-op__queuesub {
  font-size: 13.5px;
  color: #707876;
}

.ops-op__queuewarn {
  font-size: 13.5px;
  font-weight: 700;
  color: #b45309;
}

// ── other rooms ──
.ops-op__otherstitle {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #707876;
}

.ops-op__othersgrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
}

.ops-op__other {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 9px 10px 9px 14px;
  overflow: hidden;
  border: 1px solid #dfe4e3;
  border-radius: 12px;
  background: #ffffff;
  font-family: inherit;
  text-align: left;
  color: #0c0f0e;
  cursor: pointer;

  &:hover { border-color: #0f7259; }
}

.ops-op__otheredge {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
}

.ops-op__othername {
  font-size: 12.5px;
  font-weight: 800;
}

.ops-op__otherrow {
  display: flex;
  gap: 6px;
  font-size: 13px;
}

.ops-op__otherwho {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ops-op__otherwho--open { color: #707876; }

.ops-op__othertime {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

// A phone-width window: the panel stacks and the buttons sit side by side under it.
@media (max-width: 699px) {
  .ops-op__stage { flex-direction: column; }

  .ops-op__actions {
    width: auto;
    flex-direction: row;

    .ops-op__btn { flex: 1; }
  }
}
</style>
